/**
 * Camera / microphone (from Ratomeet)
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import config from './config';

export const useMediaDevices = () => {
  const [localStream, setLocalStream] = useState(null);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [error, setError] = useState(null);
  const streamRef = useRef(null);

  const getUserMedia = useCallback(async (videoEnabled = true, audioEnabled = true) => {
    try {
      setError(null);
      const constraints = {
        video: videoEnabled ? config.VIDEO_CONSTRAINTS : false,
        audio: audioEnabled ? config.AUDIO_CONSTRAINTS : false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      setLocalStream(stream);
      setIsVideoEnabled(videoEnabled);
      setIsAudioEnabled(audioEnabled);
      return stream;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const toggleVideo = useCallback(() => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoEnabled(videoTrack.enabled);
      }
    }
  }, [localStream]);

  const toggleAudio = useCallback(() => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioEnabled(audioTrack.enabled);
      }
    }
  }, [localStream]);

  const stopMedia = useCallback(() => {
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
      setLocalStream(null);
    }
    streamRef.current = null;
  }, [localStream]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  return {
    localStream,
    isVideoEnabled,
    isAudioEnabled,
    error,
    getUserMedia,
    toggleVideo,
    toggleAudio,
    stopMedia,
  };
};

export default useMediaDevices;
